/**
 * Markdown URL Parser Utility
 * Converts markdown-style URLs [text](url) to HTML links
 */

/**
 * Escapes HTML characters to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} - Escaped string
 */
function escapeHtml(str) {
	if (typeof str !== "string") {
		return "";
	}

	return str
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

/**
 * Validates if a URL is safe to use
 * @param {string} url - URL to validate
 * @returns {boolean} - True if URL is valid and safe
 */
function isValidUrl(url) {
	if (typeof url !== "string") {
		return false;
	}

	try {
		return ["http:", "https:", "mailto:"].includes(new URL(url).protocol);
	} catch {
		return false;
	}
}

/**
 * Parses markdown-style URLs in text and converts them to HTML links
 * Supports format: [link text](url)
 *
 * @param {string} text - Text containing markdown URLs
 * @returns {string} - HTML with converted links
 */
export function parseMarkdownUrls(text) {
	if (typeof text !== "string") return "";

	// Regex pattern to match [text](url) format
	// Captures: [any text](any url)
	const markdownLinkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;

	return text.replace(markdownLinkPattern, (match, linkText, url) => {
		// Trim whitespace from both parts
		const cleanLinkText = linkText.trim();
		const cleanUrl = url.trim();

		// Validate the URL
		if (!isValidUrl(cleanUrl)) {
			// If URL is invalid, return the original markdown (don't convert)
			return match;
		}

		// Escape the link text to prevent XSS
		const escapedLinkText = escapeHtml(cleanLinkText);

		// Create the HTML link with target="_blank" for external links
		return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${escapedLinkText}</a>`;
	});
}

/**
 * Converts line breaks to HTML <br> tags and parses markdown URLs
 *
 * @param {string} text - Text to process
 * @returns {string} - HTML with line breaks and converted links
 */
export function parseMarkdownText(text) {
	if (typeof text !== "string") {
		return "";
	}

	let result = parseMarkdownUrls(text);

	// Convert line breaks to <br> tags
	result = result.replace(/\r\n|\r|\n/g, "<br>");

	return result;
}

/**
 * Strips markdown syntax and returns plain text
 * Useful for fallbacks or previews
 *
 * @param {string} text - Text containing markdown
 * @returns {string} - Plain text without markdown
 */
export function stripMarkdown(text) {
	if (typeof text !== "string") return "";

	// Remove markdown link syntax, keeping only the link text
	const markdownLinkPattern = /\[([^\]]+)\]\([^)]+\)/g;
	return text.replace(markdownLinkPattern, "$1");
}
